import React, { createContext, useCallback } from 'react';
import { useOriginals } from 'hooks';
import { useOwnedItems } from 'hooks/apiTokensService';
import { CollectionItem, OwnedItem, PublicItem } from 'utils/types';
import { platformCollectionIds, mixpassOpenseaURL, wearablesOpenseaURL } from 'utils/config';
import { COLLECTION_NAMES, Original } from './common';
import { REQUEST_STATUS } from '../constants';
import { useWallet } from './WalletContext';

/**
 * Provides information about the tokens owned by the current user.
 */

interface WalletItemsContextValue {
  ownedItems: CollectionItem[];
  status: REQUEST_STATUS;
  getItem: (collectionName: string, tokenId: string) => CollectionItem | undefined;
  refetchOwnedItems: () => void;
  address: string | null;
}

const WalletItemsContext = createContext<WalletItemsContextValue>({
  ownedItems: [],
  status: REQUEST_STATUS.idle,
  getItem: () => undefined,
  refetchOwnedItems: () => undefined,
  address: null,
});

const getCollectionName = (item: PublicItem): string => {
  for (let collection in platformCollectionIds) {
    if (platformCollectionIds[collection] === item.collection_id) {
      return collection;
    }
  }
  return '';
};

const getIsPrint = (item: PublicItem, originalItem?: Original): boolean =>
  item.token_id === originalItem?.print?.id;

const getItemUrl = (item: PublicItem, collectionName: string) => {
  if (collectionName === COLLECTION_NAMES.wearable) {
    return `${wearablesOpenseaURL}/${item.contract_address}/${item.token_id}`;
  }
  if (collectionName === COLLECTION_NAMES.mixpass) {
    return `${mixpassOpenseaURL}/${item.contract_address}/${item.token_id}`;
  }
  if (collectionName === COLLECTION_NAMES.futura) {
    return `/futura/details/${item.token_id}`;
  }
  return `/${collectionName}/${item.token_id}`;
};

const getItemTarget = collectionName => {
  if (collectionName === COLLECTION_NAMES.wearable || collectionName === COLLECTION_NAMES.mixpass) {
    return '_blank';
  }
  return '_self';
};

const getCombinedStatus = (status, allStatus) => {
  let combinedStatus = status;
  if (status === REQUEST_STATUS.success && allStatus === REQUEST_STATUS.success) {
    combinedStatus = REQUEST_STATUS.success;
  }
  if (status === REQUEST_STATUS.error || allStatus === REQUEST_STATUS.error) {
    combinedStatus = REQUEST_STATUS.error;
  }
  if (status === REQUEST_STATUS.loading || allStatus === REQUEST_STATUS.loading) {
    combinedStatus = REQUEST_STATUS.loading;
  }
  if (status === REQUEST_STATUS.idle || allStatus === REQUEST_STATUS.idle) {
    combinedStatus = REQUEST_STATUS.idle;
  }
  return combinedStatus;
};

const formatItems = (ownedItems: OwnedItem[], originals: Original[]): CollectionItem[] =>
  ownedItems.map(({ item, quantity }) => {
    const collectionName = getCollectionName(item);
    const originalItem = originals.find(
      t => t.id === item.token_id || t.print?.id === item.token_id
    );

    return {
      ...item,
      quantity,
      collectionName,
      link: {
        url: getItemUrl(item, collectionName),
        target: getItemTarget(collectionName),
      },
      isPrint: getIsPrint(item, originalItem),
      original: originalItem,
    };
  });

const WalletItemsContextProvider = ({ children }: any) => {
  const { address } = useWallet();

  const { ownedItems, status, refetchOwnedItems } = useOwnedItems(address);
  // Fetch list of originals from EB api
  const { genesisEnigma, status: originalsStatus } = useOriginals();

  // TODO: use indexer for all  NFTs
  const formattedItems = formatItems(ownedItems, genesisEnigma);
  const combinedStatus = getCombinedStatus(status, originalsStatus);

  // Get item by collection name and tokenId
  const getItem = useCallback(
    (collectionName: string, tokenId: string) => {
      return formattedItems.find(
        item => item.collectionName === collectionName && item.token_id === tokenId
      );
    },
    [formattedItems]
  );

  return (
    <WalletItemsContext.Provider
      value={{
        ownedItems: formattedItems,
        status: combinedStatus,
        refetchOwnedItems,
        getItem,
        address,
      }}
    >
      {children}
    </WalletItemsContext.Provider>
  );
};

const useWalletItemsContext = () => {
  const context = React.useContext(WalletItemsContext);
  if (context === undefined) {
    throw new Error('useWalletItemsContext must be used within a WalletItemsContextProvider');
  }
  return context;
};

export { WalletItemsContextProvider, useWalletItemsContext };
